import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  assignedImageIdSet,
  createEmptyShot,
  filterShotArrangementImages,
  getAiShotImage,
  getDisplayableImageUrl,
  getEffectiveShotImage,
  getHdrEffectiveImage,
  getShotSection,
  HDR_MAX_SHOT_SIZE,
  IMAGE_CATEGORY_LABELS,
  IMAGE_CATEGORY_VALUES,
  isDirectUploadService,
  isRawFormat,
  SHOT_SECTION_KEYS,
  SHOT_SECTION_LABELS,
  SHOT_SECTION_ORDER,
  shotImageIds,
  sortEditorShotsForDisplay,
  sortImagesForDisplay,
  stabilizeSignedUrl,
} from "@beleef/editor-shot";
import { FiMaximize2 } from "react-icons/fi";
import { showToast } from "components/ui/toast";
import Modal from "components/ui/Modal";
import Button from "components/ui/Button";
import SearchSelect from "components/ui/SearchSelect";
import CompactImageCompareSlider from "./CompactImageCompareSlider";
import HelpGuide from "components/ui/HelpGuide";
import {
  AiPhotoEditProvider,
  EnhanceImageEditor,
} from "@beleef/ai-photo-edit";
import { useAiPhotoEditContextValue } from "hooks/useAiPhotoEditContextValue";

const ROOM_TYPES = [
  "Living Room",
  "Bedroom",
  "Living/Dining Room",
  "Media Room",
  "Backyard/Outdoor Space",
  "Kitchen",
  "Living/Alfresco",
];

const ENHANCEMENT_OPTIONS = [
  { label: "None", value: "" },
  { label: "Declutter", value: "declutter" },
  { label: "Virtual Furnish", value: "virtualFurnish" },
];

const ROOM_TYPE_OPTIONS = ROOM_TYPES.map((rt) => ({ label: rt, value: rt }));

const getSelectOption = (options, value) =>
  options.find((opt) => opt.value === value) || null;

const emptyAiExtras = () => ({
  enhancementType: "",
  roomType: "",
  notes: "",
  // Strip overlaid text, logos, agent stamps and copyright marks on the AI
  // pass. On by default, matching the agent-side upload switch — turned off
  // for a shot whose overlay is meant to stay.
  removeWatermark: true,
});

const vfDefaultNotes = (roomType) =>
  `Virtual Furnish - ${roomType}. Add furniture to match the aesthetic in the home`;

const Thumb = ({ image, selected, onClick, label }) => {
  const url = getDisplayableImageUrl(image);
  const canShow = !!url && !isRawFormat(url);
  return (
    <Button
      type="button"
      onClick={onClick}
      className={`w-14 h-11 rounded border overflow-hidden flex-none p-0 ${
        selected
          ? "border-black ring-2 ring-black/20"
          : "border-mediumgray bg-lightgray"
      } ${canShow ? "cursor-pointer hover:opacity-90" : "cursor-default"}`}
      title={image?.name || label}
      disabled={!onClick}
    >
      {canShow ? (
        <img
          src={url}
          alt={image?.name || label || "shot"}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <span className="text-[9px] text-darkgray px-1">{label || "…"}</span>
      )}
    </Button>
  );
};

/** Unassigned raw thumb: click assigns to shot; expand icon opens lightbox. */
const UnassignedThumb = ({ image, label, onAssign, onViewLarge }) => {
  const url = getDisplayableImageUrl(image);
  const canPreview = !!url && !isRawFormat(url) && typeof onViewLarge === "function";
  return (
    <div className="relative flex-none group">
      <Thumb image={image} label={label} onClick={onAssign} />
      {canPreview && (
        <button
          type="button"
          title="View large"
          aria-label={`View large ${image?.name || label || "image"}`}
          className="absolute -top-1 -right-1 z-10 w-[18px] h-[18px] rounded-full bg-black/75 text-white flex items-center justify-center border border-white/80 hover:bg-black"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onViewLarge(image);
          }}
        >
          <FiMaximize2 size={10} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
};

export default function ShotBoard({
  images = [],
  propertyId,
  eventId,
  api,
  existingShots = [],
  onRefresh,
  onPreview,
  onUseAsPrintMaster,
}) {
  const [shots, setShots] = useState([]);
  const [saving, setSaving] = useState(false);
  const [removingShotId, setRemovingShotId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [categoryShotId, setCategoryShotId] = useState(null);
  const [classifying, setClassifying] = useState({});
  const [printMasterBusy, setPrintMasterBusy] = useState({});
  const [helpOpen, setHelpOpen] = useState(false);
  const [adjustShotId, setAdjustShotId] = useState(null);
  const [adjustedUrlsByShotId, setAdjustedUrlsByShotId] = useState({});
  const aiPhotoEditContext = useAiPhotoEditContextValue();

  // Every shot update goes through one chain, never two at once.
  //
  // PATCH /admin/editorShots rewrites the whole editorShots array on the
  // document it just read, guarded by __v. Two overlapping writes meant the
  // first won and the rest came back 500 ("No matching document found for
  // id ... version N") — and without that guard they would have quietly
  // clobbered each other's edits. Both callers below overlap naturally: the
  // review sheet confirms every shot at once, and classification finishes
  // per-shot at whatever moment each request returns.
  //
  // Serialising here costs a round trip per shot and needs no backend
  // change. A failed write must not break the chain for the next one, hence
  // the catch on both branches.
  const shotWriteChainRef = useRef(Promise.resolve());
  const queueShotUpdate = useCallback(
    (payload) => {
      const run = shotWriteChainRef.current.then(
        () => api.updateEditorShot(payload),
        () => api.updateEditorShot(payload),
      );
      shotWriteChainRef.current = run.catch(() => {});
      return run;
    },
    [api],
  );
  const classifiedRef = useRef(new Set());
  const overrideInputRef = useRef(null);
  const [overrideShotId, setOverrideShotId] = useState(null);
  // Per-shot AI options (VF / declutter / notes) — keyed by shotId
  const [aiExtrasByShotId, setAiExtrasByShotId] = useState({});

  useEffect(() => {
    if (existingShots?.length) {
      setShots(
        existingShots.map((shot, index) => ({
          ...createEmptyShot(index),
          ...shot,
          exposureImageIds: shot.exposureImageIds || [],
        })),
      );
      setAiExtrasByShotId((prev) => {
        const next = { ...prev };
        existingShots.forEach((shot) => {
          const id = String(shot.shotId);
          if (next[id]) return;
          next[id] = {
            enhancementType: shot.ai?.enhancementType || "",
            roomType: shot.ai?.roomType || "",
            notes: shot.ai?.notes || "",
          };
        });
        return next;
      });
    } else {
      setShots([]);
    }
  }, [eventId, existingShots]);

  const getAiExtras = (shotId) =>
    aiExtrasByShotId[String(shotId)] || emptyAiExtras();

  const setAiExtras = (shotId, patch) => {
    const id = String(shotId);
    setAiExtrasByShotId((prev) => ({
      ...prev,
      [id]: { ...emptyAiExtras(), ...(prev[id] || {}), ...patch },
    }));
  };

  const buildShotEdits = (targetShots) => {
    const shotEdits = {};
    targetShots.forEach((shot) => {
      const extras = getAiExtras(shot.shotId);
      const enhancementType = extras.enhancementType || null;
      shotEdits[String(shot.shotId)] = {
        enhancementType,
        notes: extras.notes || "",
        roomType:
          enhancementType === "virtualFurnish" ? extras.roomType || null : null,
        removeWatermark: extras.removeWatermark !== false,
      };
    });
    return shotEdits;
  };

  // Slot arrangement uses all frames in the event batch.
  const arrangementImages = useMemo(
    () => filterShotArrangementImages(images),
    [images],
  );

  const imageById = useMemo(() => {
    const map = new Map();
    arrangementImages.forEach((img) => map.set(String(img.id), img));
    return map;
  }, [arrangementImages]);

  const assigned = useMemo(() => assignedImageIdSet(shots), [shots]);
  const unassigned = useMemo(
    () =>
      sortImagesForDisplay(
        arrangementImages.filter(
          (img) =>
            !assigned.has(String(img.id)) &&
            !isDirectUploadService(img.eventTypeKey) &&
            img.type !== "P",
        ),
      ),
    [arrangementImages, assigned],
  );

  const shotSections = useMemo(() => {
    const order = SHOT_SECTION_ORDER;
    const buckets = Object.fromEntries(order.map((k) => [k, []]));
    shots.forEach((shot) => {
      const main = imageById.get(String(shot.mainImageId));
      const section = getShotSection(shot, main);
      (buckets[section] || buckets.photo).push(shot);
    });
    return order
      .map((key) => ({
        key,
        label: SHOT_SECTION_LABELS[key],
        shots: sortEditorShotsForDisplay(buckets[key], imageById),
        isDirect: key === SHOT_SECTION_KEYS.drone,
      }))
      .filter((s) => s.shots.length > 0 || s.key === SHOT_SECTION_KEYS.photo);
  }, [shots, imageById]);

  const handleAiEditSection = async (sectionShots) => {
    const ready = sectionShots.filter(
      (s) => getHdrEffectiveImage(s)?.url && s.category,
    );
    if (!ready.length) {
      showToast("error", "Shots need an image + category before AI edit");
      return;
    }
    const missingRoom = ready.find((s) => {
      const extras = getAiExtras(s.shotId);
      return extras.enhancementType === "virtualFurnish" && !extras.roomType;
    });
    if (missingRoom) {
      showToast(
        "error",
        `Select a room type for Virtual Furnish on ${missingRoom.label || "shot"}`,
      );
      return;
    }
    setBusy(true);
    try {
      await api.aiEditShots({
        propertyId,
        eventId,
        shotIds: ready.map((s) => s.shotId),
        shotEdits: buildShotEdits(ready),
      });
      showToast("success", "AI edit started");
      onRefresh?.();
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message || error.message || "AI edit failed",
      );
    } finally {
      setBusy(false);
    }
  };

  // Polling for in-flight shots lives on the page-level marketing query
  // (useGetPrepareMarketingByPropertyIdQuery), not here: one ShotBoard is
  // mounted per event, so a timer here multiplied the request rate.

  // Auto-classify when HDR becomes ready
  useEffect(() => {
    if (!api?.classifyImage) return;
    shots.forEach((shot) => {
      if (!["hdrReady", "aiReady"].includes(shot.status)) return;
      if (shot.category) {
        classifiedRef.current.add(shot.shotId);
        return;
      }
      if (classifiedRef.current.has(shot.shotId)) return;
      const effective = getHdrEffectiveImage(shot);
      if (!effective?.url) return;
      classifiedRef.current.add(shot.shotId);
      setClassifying((prev) => ({ ...prev, [shot.shotId]: true }));
      api
        .classifyImage(effective.url, shot.shotId)
        .then(async (result) => {
          const category = result?.category || "INTERNAL";
          setShots((prev) =>
            prev.map((s) =>
              s.shotId === shot.shotId ? { ...s, category } : s,
            ),
          );
          if (api.updateEditorShot) {
            await queueShotUpdate({
              propertyId,
              eventId,
              shotId: shot.shotId,
              category,
            });
          }
        })
        .catch(() => {
          classifiedRef.current.delete(shot.shotId);
        })
        .finally(() => {
          setClassifying((prev) => ({ ...prev, [shot.shotId]: false }));
        });
    });
  }, [shots, api, propertyId, eventId, queueShotUpdate]);

  const persistShots = async (nextShots) => {
    if (!api?.createEditorShot) {
      showToast("error", "Shot API is not configured");
      return false;
    }
    setSaving(true);
    try {
      const payload = nextShots.map((shot, index) => ({
        shotId: shot.shotId,
        label: shot.label || `Shot ${index + 1}`,
        mainImageId: shot.mainImageId,
        exposureImageIds: shot.exposureImageIds || [],
        strength: shot.strength || 100,
        status: shot.status === "draft" ? "draft" : shot.status || "draft",
        category: shot.category || null,
        eventTypeKey: shot.eventTypeKey || null,
        hdr: shot.hdr,
        override: shot.override,
        useOverride: shot.useOverride,
        ai: shot.ai,
        printPhotoId: shot.printPhotoId,
      }));
      const saved = await api.createEditorShot({
        propertyId,
        eventId,
        shots: payload.filter((s) => s.mainImageId != null),
      });
      if (Array.isArray(saved)) {
        setShots(saved);
      }
      onRefresh?.();
      return true;
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message || error.message || "Failed to save shots",
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const addShot = () => {
    setShots((prev) => [...prev, createEmptyShot(prev.length)]);
  };

  // Removal has to be saved straight away. The parent hands existingShots
  // in as a fresh .filter() array every render, so the sync effect above
  // reloads the server list on the next parent render and a local-only
  // removal snaps back. PUT /admin/editorShots replaces the event's whole
  // list, so saving without the shot is what deletes it.
  const removeShot = async (shotId) => {
    const target = shots.find((shot) => shot.shotId === shotId);
    if (!target) return;
    const remaining = shots.filter((shot) => shot.shotId !== shotId);
    if (target.mainImageId == null) {
      // Never saved — nothing on the server to delete.
      setShots(remaining);
      return;
    }
    if (!window.confirm(`Remove ${target.label || "this shot"}?`)) return;
    // Keep the card on screen with a spinner until the save lands.
    setRemovingShotId(shotId);
    const ok = await persistShots(
      remaining.filter((shot) => shot.mainImageId != null),
    );
    setRemovingShotId(null);
    if (ok) {
      setShots((prev) => prev.filter((shot) => shot.shotId !== shotId));
      showToast("success", `${target.label || "Shot"} removed`);
    }
  };

  const setMain = (shotId, imageId) => {
    setShots((prev) =>
      prev.map((shot) => {
        if (shot.shotId !== shotId) {
          return {
            ...shot,
            exposureImageIds: (shot.exposureImageIds || []).filter(
              (id) => String(id) !== String(imageId),
            ),
            mainImageId:
              String(shot.mainImageId) === String(imageId)
                ? null
                : shot.mainImageId,
          };
        }
        const exposures = (shot.exposureImageIds || []).filter(
          (id) => String(id) !== String(imageId),
        );
        return {
          ...shot,
          mainImageId: imageId,
          exposureImageIds: exposures,
          status: "draft",
        };
      }),
    );
  };

  const toggleExposure = (shotId, imageId) => {
    setShots((prev) =>
      prev.map((shot) => {
        if (String(shot.mainImageId) === String(imageId)) return shot;
        if (shot.shotId !== shotId) {
          return {
            ...shot,
            exposureImageIds: (shot.exposureImageIds || []).filter(
              (id) => String(id) !== String(imageId),
            ),
            mainImageId:
              String(shot.mainImageId) === String(imageId)
                ? null
                : shot.mainImageId,
          };
        }
        const ids = shot.exposureImageIds || [];
        const exists = ids.some((id) => String(id) === String(imageId));
        if (exists) {
          return {
            ...shot,
            exposureImageIds: ids.filter(
              (id) => String(id) !== String(imageId),
            ),
            status: "draft",
          };
        }
        if (shotImageIds({ ...shot, exposureImageIds: [...ids, imageId] }).length >
          HDR_MAX_SHOT_SIZE) {
          showToast(
            "error",
            `A shot can contain at most ${HDR_MAX_SHOT_SIZE} images`,
          );
          return shot;
        }
        return {
          ...shot,
          exposureImageIds: [...ids, imageId],
          status: "draft",
        };
      }),
    );
  };

  const handleSave = async () => {
    const incomplete = shots.filter((s) => s.mainImageId == null);
    if (incomplete.length) {
      showToast("error", "Every shot needs a main image");
      return;
    }
    const ok = await persistShots(shots);
    if (ok) showToast("success", "Shots saved");
  };

  const handleFinalize = async () => {
    const withMain = shots.filter((s) => s.mainImageId != null);
    if (!withMain.length) {
      showToast("error", "Add at least one shot with a main image");
      return;
    }
    setBusy(true);
    try {
      const saved = await persistShots(withMain);
      if (!saved) return;
      await api.finalizeShots({ propertyId, eventId });
      showToast("success", "HDR merge started for finalized shots");
      onRefresh?.();
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message ||
          error.message ||
          "Failed to finalize shots",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleMergeOne = async (shot) => {
    setBusy(true);
    try {
      await persistShots(shots.filter((s) => s.mainImageId != null));
      await api.finalizeShots({
        propertyId,
        eventId,
        shotId: shot.shotId,
      });
      showToast("success", `HDR started for ${shot.label || "shot"}`);
      onRefresh?.();
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message || error.message || "HDR merge failed",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleOverrideFile = async (file) => {
    if (!overrideShotId || !file || !api?.uploadOverride) return;
    setBusy(true);
    try {
      await api.uploadOverride({
        propertyId,
        eventId,
        shotId: overrideShotId,
        file,
      });
      showToast("success", "Manual HDR uploaded");
      setOverrideShotId(null);
      onRefresh?.();
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message ||
          error.message ||
          "Override upload failed",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleCategorySelect = async (category) => {
    const shotId = categoryShotId;
    setCategoryShotId(null);
    if (!shotId) return;
    setShots((prev) =>
      prev.map((s) => (s.shotId === shotId ? { ...s, category } : s)),
    );
    try {
      await queueShotUpdate({ propertyId, eventId, shotId, category });
      onRefresh?.();
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message || error.message || "Failed to save category",
      );
    }
  };

  const handleAiEdit = async () => {
    const ready = shots.filter(
      (s) => getHdrEffectiveImage(s)?.url && s.category,
    );
    if (!ready.length) {
      showToast("error", "All shots need HDR + category before AI edit");
      return;
    }
    const missingRoom = ready.find((s) => {
      const extras = getAiExtras(s.shotId);
      return extras.enhancementType === "virtualFurnish" && !extras.roomType;
    });
    if (missingRoom) {
      showToast(
        "error",
        `Select a room type for Virtual Furnish on ${missingRoom.label || "shot"}`,
      );
      return;
    }
    setBusy(true);
    try {
      await api.aiEditShots({
        propertyId,
        eventId,
        shotEdits: buildShotEdits(ready),
      });
      showToast("success", "AI edit started");
      onRefresh?.();
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message || error.message || "AI edit failed",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleAiEditOne = async (shot) => {
    if (!getHdrEffectiveImage(shot)?.url || !shot.category) {
      showToast("error", "This shot needs HDR + category before AI edit");
      return;
    }
    const extras = getAiExtras(shot.shotId);
    if (extras.enhancementType === "virtualFurnish" && !extras.roomType) {
      showToast("error", "Select a room type for Virtual Furnish");
      return;
    }
    setBusy(true);
    try {
      await api.aiEditShots({
        propertyId,
        eventId,
        shotIds: [shot.shotId],
        shotEdits: buildShotEdits([shot]),
      });
      showToast("success", `AI edit started for ${shot.label || "shot"}`);
      onRefresh?.();
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message || error.message || "AI edit failed",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleUseAsPrintMasterClick = async (shot) => {
    if (!onUseAsPrintMaster) return;
    const ai = getAiShotImage(shot);
    if (!ai?.url) {
      showToast(
        "error",
        "Run AI edit before using as print master — HDR alone cannot be used",
      );
      return;
    }
    if (printMasterBusy[shot.shotId]) return;
    setPrintMasterBusy((prev) => ({ ...prev, [shot.shotId]: true }));
    try {
      await onUseAsPrintMaster(shot, ai.url);
    } finally {
      setPrintMasterBusy((prev) => {
        const next = { ...prev };
        delete next[shot.shotId];
        return next;
      });
    }
  };

  const openPreview = (shot, kind = "effective") => {
    if (!onPreview) return;
    if (kind === "inputs") {
      const items = shotImageIds(shot)
        .map((id) => imageById.get(String(id)))
        .filter(Boolean)
        .map((img) => {
          const url = getDisplayableImageUrl(img);
          if (!url || isRawFormat(url)) return null;
          return { url, name: img.name, id: img.id };
        })
        .filter(Boolean);
      onPreview(items, 0);
      return;
    }
    if (kind === "compare" || kind === "compare-ai") {
      const hdr = getHdrEffectiveImage(shot);
      const ai = getAiShotImage(shot);
      if (!ai?.url) return;
      const main = imageById.get(String(shot.mainImageId));
      const isDirect = isDirectUploadService(
        shot.eventTypeKey || main?.eventTypeKey,
      );
      const beforeUrl = stabilizeSignedUrl(
        (isDirect ? getDisplayableImageUrl(main) : null) ||
          hdr?.url ||
          ai.sourcePreviewUrl,
      );
      if (!beforeUrl || isRawFormat(beforeUrl)) return;
      const label = shot.label || shot.shotId;
      onPreview(
        [
          {
            url: beforeUrl,
            name: `${label} · ${isDirect ? "Original" : "HDR"}`,
          },
          {
            url: stabilizeSignedUrl(ai.url),
            name: `${label} · AI`,
          },
        ],
        kind === "compare-ai" ? 1 : 0,
      );
      return;
    }
    const effective =
      kind === "hdr"
        ? getHdrEffectiveImage(shot)
        : getEffectiveShotImage(shot);
    if (!effective?.url) return;
    onPreview(
      [
        {
          url: stabilizeSignedUrl(effective.url),
          name: shot.label || shot.shotId,
        },
      ],
      0,
    );
  };

  const openUnassignedPreview = (image) => {
    if (!onPreview || !image) return;
    const items = unassigned
      .map((img) => {
        const url = getDisplayableImageUrl(img);
        if (!url || isRawFormat(url)) return null;
        return { url, name: img.name, id: img.id };
      })
      .filter(Boolean);
    if (!items.length) {
      showToast("error", "Image is not ready to preview yet");
      return;
    }
    const index = items.findIndex(
      (img) => String(img.id) === String(image.id),
    );
    onPreview(items, index >= 0 ? index : 0);
  };

  if (!arrangementImages.length) return null;

  return (
    <section className="mt-5 border border-mediumgray rounded-[12px] p-4 bg-lightergray">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-[12px] font-bold uppercase tracking-[0.1em] text-darkergray">
            Editor shots
          </h3>
          <p className="text-[12px] text-darkgray mt-1 mb-0">
            Build shots with one main + exposures, finalize for HDR, set
            category, then send to AI edit.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="text-xs font-semibold text-darkergray hover:text-black border-0 bg-transparent cursor-pointer px-2 py-1"
          >
            How it works
          </button>
          <Button type="button" onClick={addShot} className="outline-button">
            Add shot
          </Button>
          <Button
            type="button"
            disabled={busy || saving}
            loading={saving}
            onClick={handleSave}
            className="outline-button"
          >
            Save shots
          </Button>
          <Button
            type="button"
            disabled={busy || saving}
            onClick={handleFinalize}
            className="black-button"
          >
            {busy ? "Working…" : "Re-run HDR for all"}
          </Button>
          <Button
            type="button"
            disabled={busy || saving}
            onClick={handleAiEdit}
            className="outline-button"
          >
            Send to AI edit
          </Button>
        </div>
      </div>

      {unassigned.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] font-bold uppercase tracking-wide text-darkgray mb-2">
            Unassigned raw ({unassigned.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {unassigned.map((image) => (
              <UnassignedThumb
                key={image.id}
                image={image}
                label={image.type || "raw"}
                onViewLarge={openUnassignedPreview}
                onAssign={() => {
                  if (!shots.length) {
                    const shot = createEmptyShot(0);
                    shot.mainImageId = image.id;
                    setShots([shot]);
                    return;
                  }
                  const draft =
                    shots.find((s) => !s.mainImageId) || shots[shots.length - 1];
                  if (!draft.mainImageId) setMain(draft.shotId, image.id);
                  else toggleExposure(draft.shotId, image.id);
                }}
              />
            ))}
          </div>
          <p className="text-[11px] text-darkgray mt-1.5 mb-0">
            Click an unassigned image to set as main (or exposure) on the latest
            shot. Use the expand icon to view large.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-5 mt-4">
        {shotSections.map((section) => (
          <div key={section.key}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <h4 className="text-[11px] font-bold uppercase tracking-[0.08em] text-darkergray m-0">
                {section.label}
                <span className="ml-1.5 font-mono text-darkgray font-normal">
                  ({section.shots.length})
                </span>
              </h4>
              {section.isDirect && section.shots.length > 0 && (
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => handleAiEditSection(section.shots)}
                  className="outline-button !py-1 !px-2.5 !text-[11px]"
                >
                  Send section to AI edit
                </Button>
              )}
            </div>
            {!section.shots.length && section.key === SHOT_SECTION_KEYS.photo && (
              <p className="text-[12px] text-darkgray mb-0">
                No photo shots yet — click Add shot or an unassigned raw image.
              </p>
            )}
            <div className="flex flex-col gap-3">
              {section.shots.map((shot) => {
          const main = imageById.get(String(shot.mainImageId));
          const pairedDuskShot =
            shot.category === "GOLDEN_HOUR"
              ? shots.find((candidate) => {
                  if (candidate.category === "GOLDEN_HOUR") return false;
                  if (
                    candidate.eventTypeKey !== "duskGolden" &&
                    candidate.category !== "DUSK"
                  ) {
                    return false;
                  }
                  if (shot.pairedDuskMainImageId != null) {
                    return (
                      String(candidate.mainImageId) ===
                      String(shot.pairedDuskMainImageId)
                    );
                  }
                  // Fallback when pair id was not persisted
                  const duskMain = imageById.get(
                    String(candidate.mainImageId),
                  );
                  return (
                    !!main &&
                    !!duskMain &&
                    (main.name === duskMain.name || main.url === duskMain.url)
                  );
                })
              : null;
          const exposureIds =
            (shot.exposureImageIds || []).length > 0
              ? shot.exposureImageIds
              : pairedDuskShot?.exposureImageIds || [];
          const exposures = (exposureIds || [])
            .map((id) => imageById.get(String(id)))
            .filter(Boolean);
          const effective = getEffectiveShotImage(shot);
          // Prefer own HDR; if still on original while dusk pair has a merge, use pair
          const ownHdr = getHdrEffectiveImage(shot);
          const pairedHdr = pairedDuskShot
            ? getHdrEffectiveImage(pairedDuskShot)
            : null;
          const hdrEffective =
            shot.category === "GOLDEN_HOUR" &&
            pairedHdr?.url &&
            (!ownHdr?.url ||
              ownHdr.singleFrame ||
              (main?.url && ownHdr.url === main.url))
              ? pairedHdr
              : ownHdr;
          const aiImage = getAiShotImage(shot);
          // Dusk & Golden Hour always shows Main + Exposures chrome
          const isDirect = section.isDirect;
          const beforeDisplay =
            stabilizeSignedUrl(
              (isDirect ? getDisplayableImageUrl(main) : null) ||
                hdrEffective?.url ||
                aiImage?.sourcePreviewUrl ||
                null,
            ) || null;
          const aiDisplayUrl = stabilizeSignedUrl(aiImage?.url) || null;
          const effectiveDisplayUrl =
            stabilizeSignedUrl(effective?.url) || null;
          const hdrDisplayUrl =
            stabilizeSignedUrl(hdrEffective?.url) || null;
          const beforeReady =
            !!beforeDisplay && !isRawFormat(beforeDisplay);
          const canCompare =
            beforeReady &&
            !!aiDisplayUrl &&
            beforeDisplay !== aiDisplayUrl;
          const aiExtras = getAiExtras(shot.shotId);
          const isPrintMaster = shot.printPhotoId != null;
          const statusChrome =
            shot.status === "hdrFailed"
              ? { label: "HDR FAILED", className: "text-red-600" }
              : isPrintMaster
                ? { label: "PRINT MASTER", className: "text-black" }
                : shot.status === "aiReady" || aiDisplayUrl
                  ? { label: "AIREADY", className: "text-darkergray" }
                  : {
                      label: String(shot.status || "draft").toUpperCase(),
                      className: "text-darkgray",
                    };
          const canAiEdit =
            !!hdrEffective?.url &&
            !!shot.category &&
            ["hdrReady", "aiReady", "aiFailed"].includes(shot.status);
          return (
            <React.Fragment key={shot.shotId}>
            <div
              className={`bg-white border rounded-2xl overflow-hidden ${
                isPrintMaster ? "border-black" : "border-mediumgray"
              }`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap px-4 py-3 border-b border-mediumgray">
                <div className="flex items-center gap-3 flex-wrap min-w-0">
                  <input
                    className="font-mono text-[12px] font-bold border border-mediumgray rounded px-2 py-1 max-w-[200px]"
                    value={shot.label || ""}
                    onChange={(e) =>
                      setShots((prev) =>
                        prev.map((s) =>
                          s.shotId === shot.shotId
                            ? { ...s, label: e.target.value }
                            : s,
                        ),
                      )
                    }
                  />
                  {isPrintMaster && (
                    <span className="flex items-center gap-1 text-[11px] font-bold text-green-700 font-mono">
                      ✓ Added as print master
                    </span>
                  )}
                </div>
                <span
                  className={`font-mono text-[10px] font-bold uppercase tracking-wide ${statusChrome.className}`}
                >
                  {statusChrome.label}
                </span>
              </div>
              <div className="p-4">
              {shot.status === "hdrFailed" && (
                <div className="mb-2 flex flex-wrap items-center gap-2 rounded border border-red-200 bg-red-50 px-2 py-1.5">
                  <span className="text-[11px] text-red-700 flex-1 min-w-0">
                    HDR failed
                    {shot.hdr?.error ? `: ${shot.hdr.error}` : ""}. Fix bracket
                    exposures if needed, then retry.
                  </span>
                  <Button
                    type="button"
                    disabled={busy || !shot.mainImageId}
                    onClick={() => handleMergeOne(shot)}
                    className="outline-button !border-red-700 !text-red-700 !py-1 !px-2.5 !text-[11px]"
                  >
                    Retry HDR
                  </Button>
                </div>
              )}

              <div className="flex gap-4 flex-wrap items-start">
                <div>
                  <div className="text-[10px] font-bold uppercase text-darkgray mb-1">
                    {isDirect ? "Original" : "Main + Exposures"}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                  {main ? (
                    <Thumb
                      image={main}
                      onClick={() => openPreview(shot, "inputs")}
                    />
                  ) : (
                    <div className="w-14 h-11 border border-dashed border-mediumgray rounded text-[10px] text-darkgray flex items-center justify-center">
                      Pick
                    </div>
                  )}
                {!isDirect &&
                  exposures.map((image) => (
                    <Thumb
                      key={image.id}
                      image={image}
                      selected
                      onClick={() => toggleExposure(shot.shotId, image.id)}
                    />
                  ))}
                  </div>
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="text-[10px] font-bold uppercase text-darkgray mb-1">
                    {canCompare
                      ? isDirect
                        ? "Original / AI Edit"
                        : "HDR / AI Edit"
                      : "Result"}
                  </div>
                  {canCompare ? (
                    <CompactImageCompareSlider
                      beforeUrl={beforeDisplay}
                      afterUrl={
                        adjustedUrlsByShotId[shot.shotId] || aiDisplayUrl
                      }
                      beforeLabel={
                        isDirect
                          ? "ORIGINAL"
                          : hdrEffective.source === "override"
                            ? "UPLOAD"
                            : "HDR"
                      }
                      afterLabel="AI"
                      onImageClick={(side) => {
                        if (
                          side === "after" &&
                          adjustedUrlsByShotId[shot.shotId] &&
                          onPreview
                        ) {
                          onPreview(
                            [
                              {
                                url: adjustedUrlsByShotId[shot.shotId],
                                name: `${shot.label || shot.shotId} · AI`,
                              },
                            ],
                            0,
                          );
                          return;
                        }
                        openPreview(
                          shot,
                          side === "after" ? "compare-ai" : "compare",
                        );
                      }}
                    />
                  ) : effectiveDisplayUrl ? (
                    <Button
                      type="button"
                      className="w-full max-w-[560px] aspect-[16/9] rounded-xl border border-mediumgray overflow-hidden p-0"
                      onClick={() => openPreview(shot)}
                    >
                      <img
                        src={
                          adjustedUrlsByShotId[shot.shotId] ||
                          getDisplayableImageUrl({
                            url: effectiveDisplayUrl,
                            originalUrl: main
                              ? getDisplayableImageUrl(main)
                              : undefined,
                          }) ||
                          effectiveDisplayUrl
                        }
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </Button>
                  ) : (
                    <div className="w-full max-w-[280px] aspect-[16/9] border border-dashed border-mediumgray rounded-xl" />
                  )}
                </div>
              </div>

              {canAiEdit && (
                <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,160px)_minmax(0,160px)_1fr]">
                  <label className="block min-w-0">
                    <span className="text-[10px] font-bold uppercase text-darkgray">
                      Enhancement
                    </span>
                    <div className="mt-1">
                      <SearchSelect
                        options={ENHANCEMENT_OPTIONS}
                        value={getSelectOption(
                          ENHANCEMENT_OPTIONS,
                          aiExtras.enhancementType,
                        )}
                        disabled={busy || shot.status === "aiProcessing"}
                        searchable={false}
                        placeholder="None"
                        onChange={(opt) => {
                          const enhancementType = opt?.value ?? "";
                          setAiExtras(shot.shotId, {
                            enhancementType,
                            roomType:
                              enhancementType === "virtualFurnish"
                                ? aiExtras.roomType
                                : "",
                          });
                        }}
                      />
                    </div>
                  </label>
                  {aiExtras.enhancementType === "virtualFurnish" && (
                    <label className="block min-w-0">
                      <span className="text-[10px] font-bold uppercase text-darkgray">
                        Room type
                      </span>
                      <div className="mt-1">
                        <SearchSelect
                          options={ROOM_TYPE_OPTIONS}
                          value={getSelectOption(
                            ROOM_TYPE_OPTIONS,
                            aiExtras.roomType,
                          )}
                          disabled={busy || shot.status === "aiProcessing"}
                          placeholder="Select room…"
                          isClearable
                          onChange={(opt) => {
                            const roomType = opt?.value ?? "";
                            const prevNotes = aiExtras.notes || "";
                            const wasDefault =
                              !prevNotes ||
                              ROOM_TYPES.some(
                                (rt) => prevNotes === vfDefaultNotes(rt),
                              );
                            setAiExtras(shot.shotId, {
                              roomType,
                              notes:
                                roomType && wasDefault
                                  ? vfDefaultNotes(roomType)
                                  : prevNotes,
                            });
                          }}
                        />
                      </div>
                    </label>
                  )}
                  <label
                    className={`block min-w-0 ${
                      aiExtras.enhancementType === "virtualFurnish"
                        ? ""
                        : "sm:col-span-2"
                    }`}
                  >
                    <span className="text-[10px] font-bold uppercase text-darkgray">
                      Notes
                    </span>
                    <textarea
                      className="mt-1 form-textarea border border-mediumgray"
                      rows={5}
                      placeholder="Optional instructions for AI edit…"
                      value={aiExtras.notes}
                      disabled={busy || shot.status === "aiProcessing"}
                      onChange={(e) =>
                        setAiExtras(shot.shotId, { notes: e.target.value })
                      }
                    />
                  </label>
                  <label className="flex items-start gap-2 sm:col-span-3">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={aiExtras.removeWatermark !== false}
                      disabled={busy || shot.status === "aiProcessing"}
                      onChange={(e) =>
                        setAiExtras(shot.shotId, {
                          removeWatermark: e.target.checked,
                        })
                      }
                    />
                    <span className="text-[11px] text-darkgray leading-snug">
                      Remove watermarks — clears overlaid text, logos, agent
                      stamps and copyright marks. Uncheck to keep them.
                    </span>
                  </label>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-mediumgray items-center">
                {!isDirect && (
                  <>
                    <Button
                      type="button"
                      disabled={busy || !shot.mainImageId}
                      onClick={() => handleMergeOne(shot)}
                      className="outline-button !py-1 !px-2.5 !text-[11px]"
                    >
                      Re-run HDR merge
                    </Button>
                    <Button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setOverrideShotId(shot.shotId);
                        overrideInputRef.current?.click();
                      }}
                      className="outline-button !py-1 !px-2.5 !text-[11px]"
                    >
                      Upload HDR
                    </Button>
                  </>
                )}
                <Button
                  type="button"
                  onClick={() => setCategoryShotId(shot.shotId)}
                  className="outline-button !py-1 !px-2.5 !text-[11px]"
                >
                  {classifying[shot.shotId]
                    ? "Classifying…"
                    : shot.category
                      ? IMAGE_CATEGORY_LABELS[shot.category] || shot.category
                      : "Set category"}
                </Button>
                {(hdrDisplayUrl || aiDisplayUrl) && (
                  <Button
                    type="button"
                    disabled={
                      !!printMasterBusy[shot.shotId] || !aiDisplayUrl
                    }
                    loading={!!printMasterBusy[shot.shotId]}
                    title={
                      aiDisplayUrl
                        ? "Use AI result as print master"
                        : "AI edit required before print master"
                    }
                    onClick={() => handleUseAsPrintMasterClick(shot)}
                    className={`${
                      isPrintMaster ? "black-button" : "outline-button"
                    } !py-1 !px-2.5 !text-[11px]`}
                  >
                    {isPrintMaster ? "✓ Print master" : "Use as print master"}
                  </Button>
                )}
                {hdrDisplayUrl &&
                  shot.category &&
                  ["hdrReady", "aiReady", "aiFailed"].includes(shot.status) && (
                    <Button
                      type="button"
                      disabled={busy || shot.status === "aiProcessing"}
                      onClick={() => handleAiEditOne(shot)}
                      className="outline-button !py-1 !px-2.5 !text-[11px]"
                    >
                      {shot.status === "aiFailed"
                        ? "Retry AI edit"
                        : "Send to AI edit"}
                    </Button>
                  )}
                {(aiDisplayUrl || hdrDisplayUrl) && (
                  <Button
                    type="button"
                    disabled={!aiDisplayUrl && !hdrDisplayUrl}
                    onClick={() => setAdjustShotId(shot.shotId)}
                    className="outline-button !py-1 !px-2.5 !text-[11px]"
                  >
                    Adjust image
                  </Button>
                )}
                {hdrDisplayUrl && (
                  <a
                    href={
                      isDirect
                        ? getDisplayableImageUrl(main) || hdrDisplayUrl
                        : hdrDisplayUrl
                    }
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="outline-button !py-1 !px-2.5 !text-[11px] inline-flex items-center"
                  >
                    {isDirect ? "Download original" : "Download HDR"}
                  </a>
                )}
                {!isDirect && (
                  <Button
                    type="button"
                    disabled={busy || saving}
                    loading={removingShotId === shot.shotId}
                    onClick={() => removeShot(shot.shotId)}
                    className="ml-auto gray-button !py-1 !px-2.5 !text-[11px]"
                  >
                    Remove
                  </Button>
                )}
              </div>
              {!isDirect && unassigned.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {unassigned.slice(0, 12).map((image) => (
                    <UnassignedThumb
                      key={`${shot.shotId}-${image.id}`}
                      image={image}
                      label={image.type}
                      onViewLarge={openUnassignedPreview}
                      onAssign={() => {
                        if (!shot.mainImageId) setMain(shot.shotId, image.id);
                        else toggleExposure(shot.shotId, image.id);
                      }}
                    />
                  ))}
                </div>
              )}
              </div>
            </div>
            {section.key === SHOT_SECTION_KEYS.photo && (
              <div className="flex justify-start">
                <Button
                  type="button"
                  onClick={addShot}
                  className="outline-button !py-1.5 !px-3 !text-[12px] !border-dashed"
                >
                  Add shot
                </Button>
              </div>
            )}
            </React.Fragment>
          );
              })}
            </div>
          </div>
        ))}
      </div>

      <input
        ref={overrideInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleOverrideFile(file);
          e.target.value = "";
        }}
      />

      <Modal
        isOpen={categoryShotId != null}
        onClose={() => setCategoryShotId(null)}
        title="Change category"
        className="max-w-sm"
      >
        <div className="space-y-1.5">
          {IMAGE_CATEGORY_VALUES.map((value) => {
            const shot = shots.find((s) => s.shotId === categoryShotId);
            const active = shot?.category === value;
            return (
              <Button
                key={value}
                type="button"
                onClick={() => handleCategorySelect(value)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors text-left ${
                  active
                    ? "bg-lightergray border-black"
                    : "bg-white border-mediumgray hover:bg-lightergray"
                }`}
              >
                <span
                  className={`text-sm ${
                    active
                      ? "font-semibold text-black"
                      : "text-darkergray font-normal"
                  }`}
                >
                  {IMAGE_CATEGORY_LABELS[value]}
                </span>
              </Button>
            );
          })}
        </div>
      </Modal>
      {helpOpen && (
        <HelpGuide mode="editor" onClose={() => setHelpOpen(false)} />
      )}
      {adjustShotId &&
        (() => {
          const shot = shots.find((s) => s.shotId === adjustShotId);
          const aiUrl =
            adjustedUrlsByShotId[adjustShotId] ||
            stabilizeSignedUrl(getAiShotImage(shot)?.url) ||
            stabilizeSignedUrl(getHdrEffectiveImage(shot)?.url) ||
            "";
          if (!shot || !aiUrl) return null;
          return (
            <AiPhotoEditProvider value={aiPhotoEditContext}>
              <EnhanceImageEditor
                isOpen
                onClose={() => setAdjustShotId(null)}
                imageUrl={aiUrl}
                propertyId={propertyId}
                uploadFolderPath="photos/editedPrint"
                onSaved={(finalUrl) => {
                  setAdjustedUrlsByShotId((prev) => ({
                    ...prev,
                    [adjustShotId]: finalUrl,
                  }));
                  setAdjustShotId(null);
                  showToast("success", "Enhanced image saved");
                  onRefresh?.();
                }}
              />
            </AiPhotoEditProvider>
          );
        })()}
    </section>
  );
}
